import React, { useEffect, useState } from 'react';
import { Input, Menu, Layout, Select, Form, Typography, Row, Col, Slider, Switch } from 'antd';
import './HeaderComponent.css';
import { usePermissions } from './PermissionsProvider';
import { ConfigService } from './services/ConfigService';
import ModelInput from './ModelInput';

// Define an interface for the component props
interface ConfigComponentProps {
    models: string[];
    setModels: (models: string[]) => void;
    temperature: number;
    setTemperature: (temperature: number) => void;
    evaluations: boolean;
    setEvaluations: (evaluations: boolean) => void;
    autocorrect: boolean;
    setAutocorrect: (autocorrect: boolean) => void;
    retrieveContext: boolean;
    setRetrieveContext: (retrieveContext: boolean) => void;
    streaming: boolean;
    setStreaming: (streaming: boolean) => void;
}

const ConfigComponent: React.FC<ConfigComponentProps> = ({ models, setModels, temperature, setTemperature, evaluations, setEvaluations, autocorrect, setAutocorrect, retrieveContext, setRetrieveContext, streaming, setStreaming }) => {
    const { apiKey } = usePermissions();
    const [availableModels, setAvailableModels] = useState<string[]>([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        // When evaluations is off, ensure autocorrect is off
        if (!evaluations) {
            setAutocorrect(false);
        }
    }, [evaluations, setAutocorrect]);

    const groupModelsByProvider = (models: string[]) => {
        const grouped: { [provider: string]: string[] } = {};
        models.forEach((model) => {
            const [provider] = model.split(":");
            if (!grouped[provider]) {
                grouped[provider] = [];
            }
            grouped[provider].push(model);
        });
        return grouped;
    };

    const capitalizeFirstLetter = (string: string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    // Handler for model change that ensures we always have values as an array
    const handleModelChange = (value: string | string[]) => {
        if (Array.isArray(value)) {
            setModels(value);
        } else if (value) {
            setModels([value]);
        } else {
            setModels([]);
        }
    };

    // Responsive column spans based on screen size
    const labelSpan = isMobile ? 24 : 10;
    const controlSpan = isMobile ? 24 : 14;
    
    // Responsive container styles
    const containerStyle = {
        width: "100%", 
        display: "flex", 
        flexDirection: "column" as const, 
        alignItems: "start", 
        background: "rgba(0,0,0,0.02)", 
        borderRadius: "10px", 
        padding: isMobile ? "10px 8px" : "20px 20px",
        gap: isMobile ? "10px" : "20px",
        boxSizing: "border-box" as const,
        maxWidth: "100%"
    };

    return (
        <div style={containerStyle}>
            <Row style={{width: "100%"}}>
                <Col span={labelSpan} style={{marginBottom: isMobile ? "5px" : "0"}}>
                    <Typography style={{fontSize: "14px", fontWeight: "600", color: "rgba(0,0,0,0.8)", textAlign: "start"}}>Models</Typography>
                </Col>
                <Col span={controlSpan}>
                    <ModelInput
                        value={models}
                        onChange={handleModelChange}
                        placeholder="Select models"
                        label=""
                        showEmptyOption={true}
                        modelType="all"
                        mode="multiple"
                    />
                </Col>
            </Row>
            <Row style={{width: "100%"}}>
                <Col span={labelSpan} style={{marginBottom: isMobile ? "5px" : "0"}}>
                    <Typography style={{fontSize: "14px", fontWeight: "600", color: "rgba(0,0,0,0.8)", textAlign: "start"}}>Temperature</Typography>
                </Col>
                <Col span={controlSpan}>
                    <Slider
                        min={0}
                        max={2}
                        className="cus-slider-wrap"
                        value={temperature}
                        step={0.01}
                        onChange={(value) => setTemperature(value)}
                        onChangeComplete={(value) => setTemperature(value)}
                    />
                </Col>
            </Row>
            <Row style={{width: "100%"}}>
                <Col span={labelSpan} style={{marginBottom: isMobile ? "5px" : "0"}}>
                    <Typography style={{fontSize: "14px", fontWeight: "600", color: "rgba(0,0,0,0.8)", textAlign: "start"}}>Streaming</Typography>
                </Col>
                <Col span={controlSpan} style={{display: "flex", justifyContent: isMobile ? "start" : "end"}}>
                    <Switch
                        checked={streaming}
                        onChange={(value) => setStreaming(value)}
                    />
                </Col>
            </Row>
            {/* <Row style={{width: "100%"}}>
                <Col span={labelSpan} style={{marginBottom: isMobile ? "5px" : "0"}}>
                    <Typography style={{fontSize: "14px", fontWeight: "600", color: "rgba(0,0,0,0.8)", textAlign: "start"}}>Evaluations</Typography>
                </Col>
                <Col span={controlSpan} style={{display: "flex", justifyContent: isMobile ? "start" : "end"}}>
                    <Switch
                        checked={evaluations}
                        onChange={(value) => setEvaluations(value)}
                    />
                </Col>
            </Row>
            <Row style={{width: "100%"}}>
                <Col span={labelSpan} style={{marginBottom: isMobile ? "5px" : "0"}}>
                    <Typography style={{fontSize: "14px", fontWeight: "600", color: "rgba(0,0,0,0.8)", textAlign: "start"}}>Autocorrect</Typography>
                </Col>
                <Col span={controlSpan} style={{display: "flex", justifyContent: isMobile ? "start" : "end"}}>
                    <Switch
                        checked={autocorrect}
                        onChange={(value) => setAutocorrect(value)}
                        disabled={!evaluations}
                    />
                </Col>
            </Row> */}
        </div>
    );
}

export default ConfigComponent;
